const BASE_URL = process.env.VUE_APP_PRINT_AGENT_URL || 'http://127.0.0.1:9977';

// Dos presupuestos distintos, porque son dos cosas distintas (FE-RTK-001).
//
// `HEALTH_TIMEOUT_MS`: si el agente no contesta un `/health` en 5 s, está
// caído y esperar más no aporta nada.
//
// `PRINT_TIMEOUT_MS`: un `POST /print` mantiene la conexión abierta mientras el
// agente empuja bytes a la térmica, y el agente tiene su propio presupuesto de
// 20 000 ms (`printTimeoutMs` en pos-printer/config). Antes acá había 5 000 ms
// para todo: el navegador cortaba a los 5 s a un agente que todavía estaba
// trabajando bien, y el operador veía "signal is aborted without reason".
// Ahora se espera MÁS que el agente a propósito, para que el que corte sea
// siempre él — que sí sabe por qué cortó y lo puede explicar.
const HEALTH_TIMEOUT_MS = 5000;
const PRINT_TIMEOUT_MS = 25000;

// Error con el que se aborta, en vez de un `abort()` pelado. `controller.abort()`
// sin argumento es lo que produce el `signal is aborted without reason` del
// navegador: un texto que no le dice nada a nadie. Con una razón explícita, el
// `catch` recibe algo interpretable (FE-RTK-002).
export class PrintAgentTimeoutError extends Error {
  constructor(timeoutMs) {
    super(`El agente local de impresión no respondió en ${timeoutMs} ms.`);
    this.name = 'PrintAgentTimeoutError';
    this.code = 'print_agent_timeout';
    this.timeoutMs = timeoutMs;
  }
}

function withTimeout(promiseFactory, timeoutMs = HEALTH_TIMEOUT_MS) {
  const controller = new AbortController();
  const razon = new PrintAgentTimeoutError(timeoutMs);
  const timeout = setTimeout(() => controller.abort(razon), timeoutMs);

  return promiseFactory(controller.signal)
    .catch((error) => {
      // Firefox y Safari todavía descartan el `reason` y lanzan un AbortError
      // genérico. Se normaliza acá para que el resto del sistema vea siempre
      // el mismo error, sin importar el navegador.
      if (error === razon) throw razon;
      if (error?.name === 'AbortError') throw razon;
      throw error;
    })
    .finally(() => clearTimeout(timeout));
}

async function parseResponse(response) {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch (error) {
    return text;
  }
}

function buildHeaders(extraHeaders = {}) {
  const headers = {};
  Object.entries(extraHeaders).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      headers[key] = String(value);
    }
  });
  return headers;
}

// Ventana dentro de la cual dos pedidos del MISMO ticket comparten clave y el
// agente los deduplica. Corta a propósito: fuera de ella una reimpresión
// legítima tiene que poder salir.
const IDEMPOTENCY_WINDOW_MS = 60000;

// La clave identifica EL TICKET QUE SE QUIERE EN PAPEL, no esta invocación de
// la función (FE-RTK-003).
//
// Antes acá se concatenaba un `randomUUID()` en cada llamada, así que la
// deduplicación del agente —que existe y funciona, `agent.js` responde
// `duplicate_job`— nunca se activaba: cada reintento llegaba con clave nueva y
// se imprimía de nuevo. Con el timeout que abortaba a los 5 s, eso era una
// receta para duplicar tickets: el operador ve un error, reintenta, y salen
// dos.
//
// Ahora la clave se deriva del `prefix` (que ya trae el `saleId`) y de la
// ventana temporal en curso. Dos clics seguidos comparten clave; una
// reimpresión un rato después no.
function buildIdempotencyKey(prefix = 'print', { windowMs = IDEMPOTENCY_WINDOW_MS, now = Date.now() } = {}) {
  const ventana = Math.floor(now / windowMs);
  return `${prefix}-w${ventana}`;
}

async function getHealth() {
  return withTimeout(async (signal) => {
    const response = await fetch(`${BASE_URL}/health`, { method: 'GET', signal });
    const payload = await parseResponse(response);
    if (!response.ok) {
      throw new Error(payload?.error || 'No se pudo consultar la salud del agente local');
    }
    return payload;
  });
}

async function getPrinters() {
  return withTimeout(async (signal) => {
    const response = await fetch(`${BASE_URL}/printers`, { method: 'GET', signal });
    const payload = await parseResponse(response);
    if (!response.ok) {
      throw new Error(payload?.error || 'No se pudo listar impresoras locales');
    }
    return Array.isArray(payload) ? payload : [];
  });
}

async function printJson({
  payload,
  printerName = '',
  backend = 'raw',
  copies = 1,
  cut = true,
  apiKey = '',
  idempotencyKey = null
}) {
  return withTimeout(async (signal) => {
    const headers = buildHeaders({
      'Content-Type': 'application/json',
      'X-Printer': printerName || null,
      'X-Backend': backend || 'raw',
      'X-Copies': copies,
      'X-Cut': cut ? '1' : '0',
      'X-Api-Key': apiKey || null,
      'Idempotency-Key': idempotencyKey || buildIdempotencyKey('invoice')
    });

    const response = await fetch(`${BASE_URL}/print`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      signal
    });

    const data = await parseResponse(response);
    if (!response.ok) {
      const error = new Error(data?.error || `Impresion rechazada: HTTP ${response.status}`);
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    return data || { ok: true };
  }, PRINT_TIMEOUT_MS);
}

async function printBytes({
  bytes,
  contentType = 'application/octet-stream',
  printerName = '',
  backend = 'raw',
  copies = 1,
  cut = true,
  apiKey = '',
  idempotencyKey = null
}) {
  return withTimeout(async (signal) => {
    const headers = buildHeaders({
      'Content-Type': contentType || 'application/octet-stream',
      'X-Printer': printerName || null,
      'X-Backend': backend || 'raw',
      'X-Copies': copies,
      'X-Cut': cut ? '1' : '0',
      'X-Api-Key': apiKey || null,
      'Idempotency-Key': idempotencyKey || buildIdempotencyKey('invoice')
    });

    const response = await fetch(`${BASE_URL}/print`, {
      method: 'POST',
      headers,
      body: bytes,
      signal
    });

    const data = await parseResponse(response);
    if (!response.ok) {
      const error = new Error(data?.error || `Impresion rechazada: HTTP ${response.status}`);
      error.status = response.status;
      error.payload = data;
      throw error;
    }
    return data || { ok: true };
  }, PRINT_TIMEOUT_MS);
}

export default {
  BASE_URL,
  IDEMPOTENCY_WINDOW_MS,
  buildIdempotencyKey,
  getHealth,
  getPrinters,
  printJson,
  printBytes
};
